import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useTreeViewApiRef } from '@mui/x-tree-view';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import PublicIcon from '@mui/icons-material/Public';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import TopicOutlined from '@mui/icons-material/TopicOutlined';
import FunctionsOutlined from '@mui/icons-material/FunctionsOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InventoryOutlined from '@mui/icons-material/InventoryOutlined';
import HistoryIcon from '@mui/icons-material/History';
import CachedOutlined from '@mui/icons-material/CachedOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import RecentActorsOutlinedIcon from '@mui/icons-material/RecentActorsOutlined';
import { ShoppingBasketOutlined } from '@mui/icons-material';

import AgMainNavigation from '../../stories/Components/Navigations/AgMainNavigation';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  setOrg_Redirect_URL,
  setIsOpenUnsavedModal as setOrgUnsavedModal,
} from '../../redux/slices/organizations';
import {
  setIsOpenUnsavedModal,
  setMember_Redirect_URL,
} from '../../redux/slices/member';
import {
  setIsOpenUnsavedModal as setStaffUnsavedModal,
  setStaff_Redirect_URL,
} from '../../redux/slices/staff';
import { setLocaleIsOpenUnsavedModal } from '../../redux/slices/locales';
import { setIsOpenCertificateUnsavedModal } from '../../redux/slices/certificate';
import {
  ASSET_TYPES_LIST_READ_PERMISSION,
  ASSET_TYPES_LIST_WRITE_PERMISSION,
  ASSET_TYPES_READ_PERMISSION,
  ASSET_TYPES_WRITE_PERMISSION,
  CERTIFICATE_READ_PERMISSION,
  CERTIFICATE_WRITE_PERMISSION,
  COUNTRIES_READ_PERMISSION,
  COUNTRIES_WRITE_PERMISSION,
  CUSTOM_FIELD_READ_PERMISSION,
  CUSTOM_FIELD_WRITE_PERMISSION,
  ENUM_READ_PERMISSION,
  ENUM_WRITE_PERMISSION,
  featureFlags,
  LICENSE_READ_PERMISSION,
  LICENSE_WRITE_PERMISSION,
  LIST_MANAGEMENT_EDIT_PERMISSION,
  LIST_MANAGEMENT_READ_PERMISSION,
  LOCALIZATION_PERMISSION,
  MEMBER_EDIT_PERMISSION,
  MEMBER_READ_PERMISSION,
  ORGANIZATION_IMMEDIATE_READ_PERMISSION,
  ORGANIZATION_TREE_READ_PERMISSION,
  ROOT_PATHS,
  UNIT_READ_PERMISSION,
  UNIT_WRITE_PERMISSION,
  ADMIN_LOCALE_READ_PERMISSION,
  ADMIN_LOCALE_WRITE_PERMISSION,
  CONSENT_READ_PERMISSION,
  CONSENT_WRITE_PERMISSION,
  CONTRACT_TYPES_READ_PERMISSION,
  CONTRACT_TYPES_WRITE_PERMISSION,
  TRAINING_TOPICS_READ_PERMISSION,
  TRAINING_TOPICS_WRITE_PERMISSION,
  FUNCTIONS_READ_PERMISSION,
  FUNCTIONS_WRITE_PERMISSION,
  TRAINING_READ_PERMISSION,
  TRAINING_WRITE_PERMISSION,
  BULK_UPLOAD_REQUEST_READ_PERMISSION,
  CERTIFICATE_TYPE_WRITE_PERMISSION,
  CERTIFICATE_TYPE_READ_PERMISSION,
  LOGS_GENERAL_PERMISSION,
  LOGS_USER_PERMISSION,
  BANK_READ_PERMISSION,
  BANK_WRITE_PERMISSION,
  BATCH_READ_PERMISSION,
  BATCH_WRITE_PERMISSION,
  BATCH_BUY_SELL_PERMISSION,
} from '../../Constants';

import home from '../../stories/assets/home.svg';
import reports from '../../stories/assets/assignment.svg';
import group from '../../stories/assets/group.svg';
import badge from '../../stories/assets/badge.svg';
import domain from '../../stories/assets/domain.svg';
import task from '../../stories/assets/task.svg';

const SidebarVariation1 = () => {
  const navigate = useNavigate();
  const messages = useIntl().formatMessage;
  const apiRef = useTreeViewApiRef();

  const dispatch = useAppDispatch();
  const profile = useAppSelector((state) => state.full.fullProfile);
  const user = useAppSelector((state) => state.global.users);
  const allFlags = useAppSelector((state) => state.auth.flags);
  const isDirty_Member_Personal = useAppSelector(
    (state) => state.member.isDirty_Member_Personal
  );
  const isDirty_Staff_Personal = useAppSelector(
    (state) => state.staff.isDirty_Staff_Personal
  );
  const isDirty_Org_Details = useAppSelector(
    (state) => state.orgs.isDirty_Org_Details
  );
  const hasCertificateChanged = useAppSelector(
    (state) => state.certificate.hasCertificateChanged
  );
  const locale_newLocaleFileUploaded = useAppSelector(
    (state) => state.language.locale_newLocaleFileUploaded
  );

  const currentPath = window.location.pathname;
  const license = profile?.organizations?.[0]?.license;
  const canEditCustomFields = license?.canEditCustomFields;

  const permissions = useMemo(() => user?.[0]?.permissions ?? [], [user]);

  // Permissions to show or hide the sidebar menu items
  const perms = useMemo(() => {
    const permissionSets = {
      user: {
        certificate: [
          CERTIFICATE_READ_PERMISSION,
          CERTIFICATE_WRITE_PERMISSION,
        ],
        identity: [MEMBER_EDIT_PERMISSION, MEMBER_READ_PERMISSION],
        organization: [
          ORGANIZATION_IMMEDIATE_READ_PERMISSION,
          ORGANIZATION_TREE_READ_PERMISSION,
        ],
        training: [TRAINING_READ_PERMISSION, TRAINING_WRITE_PERMISSION],
      },
      operations: {
        inventoryManagement: [
          BATCH_READ_PERMISSION,
          BATCH_WRITE_PERMISSION,
          BATCH_BUY_SELL_PERMISSION,
        ],
        paymentManager: [BANK_READ_PERMISSION, BANK_WRITE_PERMISSION],
      },
      admin: {
        enums: [ENUM_READ_PERMISSION, ENUM_WRITE_PERMISSION],
        customField: [
          CUSTOM_FIELD_READ_PERMISSION,
          CUSTOM_FIELD_WRITE_PERMISSION,
        ],
        assetTypes: [ASSET_TYPES_READ_PERMISSION, ASSET_TYPES_WRITE_PERMISSION],
        listManagement: [
          LIST_MANAGEMENT_READ_PERMISSION,
          LIST_MANAGEMENT_EDIT_PERMISSION,
        ],
        consents: [CONSENT_READ_PERMISSION, CONSENT_WRITE_PERMISSION],
        contractTypes: [
          CONTRACT_TYPES_READ_PERMISSION,
          CONTRACT_TYPES_WRITE_PERMISSION,
        ],
        trainingTopics: [
          TRAINING_TOPICS_READ_PERMISSION,
          TRAINING_TOPICS_WRITE_PERMISSION,
        ],
        functions: [FUNCTIONS_READ_PERMISSION, FUNCTIONS_WRITE_PERMISSION],
        bulkUploadRequest: [BULK_UPLOAD_REQUEST_READ_PERMISSION],
        certificateTypes: [
          CERTIFICATE_TYPE_READ_PERMISSION,
          CERTIFICATE_TYPE_WRITE_PERMISSION,
        ],
        logs: [LOGS_GENERAL_PERMISSION, LOGS_USER_PERMISSION],
      },
      configuration: {
        units: [UNIT_READ_PERMISSION, UNIT_WRITE_PERMISSION],
        countries: [COUNTRIES_READ_PERMISSION, COUNTRIES_WRITE_PERMISSION],
        cultures: [ADMIN_LOCALE_READ_PERMISSION, ADMIN_LOCALE_WRITE_PERMISSION],
        assetTypeList: [
          ASSET_TYPES_LIST_READ_PERMISSION,
          ASSET_TYPES_LIST_WRITE_PERMISSION,
        ],
        localization: [LOCALIZATION_PERMISSION],
        licenses: [LICENSE_READ_PERMISSION, LICENSE_WRITE_PERMISSION],
      },
    };

    const hasPerm = (requiredPerms: number[]) =>
      requiredPerms.some((perm) => permissions.includes(perm));
    const { admin, user, configuration, operations } = permissionSets;
    const userPerms = Object.values(user).flat();
    const operationsPerms = Object.values(operations).flat();
    const adminPerms = Object.values(admin).flat();
    const configurationPerms = Object.values(configuration).flat();

    return {
      // User permissions
      hasIdentityPerm: hasPerm(user.identity),
      hasOrgPerm: hasPerm(user.organization),
      hasCertificatePerm: hasPerm(user.certificate),
      hasTrainingPerm: hasPerm(user.training),

      // Operations permissions
      hasPaymentManagerPerm: hasPerm(operations.paymentManager),
      hasInventoryManagementPerm: hasPerm(operations.inventoryManagement),

      // Admin permissions
      hasCustomFieldPerm: hasPerm(admin.customField),
      hasAssetTypesPerm: hasPerm(admin.assetTypes),
      hasListManagementPerm: hasPerm(admin.listManagement),
      hasEnums: hasPerm(admin.enums),
      hasConsents: hasPerm(admin.consents),
      hasContractTypes: hasPerm(admin.contractTypes),
      hasTrainingTopics: hasPerm(admin.trainingTopics),
      hasFunctions: hasPerm(admin.functions),
      hasBulkUploadRequestPerm: hasPerm(admin.bulkUploadRequest),
      hasCertificateTypesPerm: hasPerm(admin.certificateTypes),
      hasLogsPerm: hasPerm(admin.logs),

      // Configuration permissions
      hasLicensePerm: hasPerm(configuration.licenses),
      hasAssetTypeListPerm: hasPerm(configuration.assetTypeList),
      hasLocalizationPerm: hasPerm(configuration.localization),
      hasCountries: hasPerm(configuration.countries),
      hasUnits: hasPerm(configuration.units),
      hasCultures: hasPerm(configuration.cultures),

      // Show admin menu if any admin permission is present
      showDataManagerPerms: hasPerm(userPerms),
      showAdminMenu: hasPerm(adminPerms),
      showConfigurationMenu: hasPerm(configurationPerms),
      showOperationsMenu: hasPerm(operationsPerms),
    };
  }, [permissions]);

  // Flags to show or hide the sidebar menu items
  const flags = useMemo(() => {
    return {
      staffOrgs: allFlags.includes(featureFlags.STAFFORGS),
      viewStaff: allFlags.includes(featureFlags.VIEWSTAFF),
      certificate: allFlags.includes(featureFlags.CERTIFICATE),
      inventoryManagement: allFlags.includes(featureFlags.INVENTORY_MANAGEMENT),
      paymentsManagement: allFlags.includes(featureFlags.PAYMENTS_MANAGEMENT),
    };
  }, [allFlags]);

  // Regex for the sidebar menu items
  const routeRegex = useMemo(() => {
    return {
      home: /^\/$/,
      reports: new RegExp(`^${ROOT_PATHS.REPORTS}(?:/\\d+)?$`),
      members: new RegExp(
        `^(?:${ROOT_PATHS.MEMBERS}|${ROOT_PATHS.ADD_MEMBER}|${ROOT_PATHS.MEMBER}/\\d+)$`
      ),
      staffs: new RegExp(
        `^(?:${ROOT_PATHS.STAFF}(?:/\\d+)?|${ROOT_PATHS.ADD_STAFF})$`
      ),
      organizations: new RegExp(
        `^(?:${ROOT_PATHS.ORGANISATIONS}|${ROOT_PATHS.ADD_ORGANISATION}|${ROOT_PATHS.ORGANISATION}/\\d+(?:/(payment-types|asset-types|asset-prices))?)$`
      ),
      certificates: new RegExp(
        `^(?:${ROOT_PATHS.CERTIFICATES}|${ROOT_PATHS.ADD_CERTIFICATE}|${ROOT_PATHS.CERTIFICATE}/\\d+)$`
      ),
      trainings: new RegExp(
        `^(?:${ROOT_PATHS.TRAININGS}|${ROOT_PATHS.ADD_TRAINING}|${ROOT_PATHS.TRAINING}/\\d+)$`
      ),
      customFields: new RegExp(
        `^(?:${ROOT_PATHS.CUSTOM_FIELDS}(?:/\\d+)?|${ROOT_PATHS.ADD_CUSTOM_FIELDS})$`
      ),
      assetTypes: new RegExp(`^${ROOT_PATHS.ASSET_TYPES}$`),
      assetTypeList: new RegExp(
        `^(?:${ROOT_PATHS.ASSET_TYPE_LIST}(?:/\\d+)?|${ROOT_PATHS.ADD_ASSET_TYPE_LIST})$`
      ),
      enums: new RegExp(
        `^(?:${ROOT_PATHS.ENUMS}(?:/\\d+)?|${ROOT_PATHS.ADD_ENUM})$`
      ),
      listManagement: new RegExp(`^${ROOT_PATHS.LIST_MANAGEMENT}$`),
      licenses: new RegExp(
        `^(?:${ROOT_PATHS.LICENSES}(?:/\\d+)?|${ROOT_PATHS.ADD_LICENSE})$`
      ),
      localization: new RegExp(`^${ROOT_PATHS.LOCALISATION}$`),
      countries: new RegExp(`^${ROOT_PATHS.COUNTRIES}$`),
      units: new RegExp(
        `^(?:${ROOT_PATHS.UNITS}(?:/\\d+)?|${ROOT_PATHS.ADD_UNIT})$`
      ),
      cultures: new RegExp(`^${ROOT_PATHS.CULTURES}$`),
      consents: new RegExp(
        `^(?:${ROOT_PATHS.CONSENTS}(?:/\\d+)?|${ROOT_PATHS.ADD_CONSENT})$`
      ),
      contractTypes: new RegExp(
        `^(?:${ROOT_PATHS.CONTRACT_TYPES}(?:/\\d+)?|${ROOT_PATHS.ADD_CONTRACT_TYPE})$`
      ),
      trainingTopics: new RegExp(
        `^(?:${ROOT_PATHS.TRAINING_TOPICS}(?:/\\d+)?|${ROOT_PATHS.ADD_TRAINING_TOPIC})$`
      ),
      functions: new RegExp(`^${ROOT_PATHS.FUNCTIONS}$`),
      bulkUploadRequest: new RegExp(
        `^${ROOT_PATHS.BULK_UPLOAD_REQUEST}(?:/.+)?$`
      ),
      onHand: new RegExp(`^${ROOT_PATHS.ON_HAND}$`),
      history: new RegExp(`^${ROOT_PATHS.HISTORY}$`),
      processing: new RegExp(`^${ROOT_PATHS.PROCESSING}$`),
      bulkPurchase: new RegExp(`^${ROOT_PATHS.BULK_PURCHASE}(?:/.+)?$`),
      certificateTypes: new RegExp(
        `^(?:${ROOT_PATHS.CERTIFICATE_TYPES}(?:/\\d+)?|${ROOT_PATHS.ADD_CERTIFICATE_TYPE})$`
      ),
      logs: new RegExp(`^${ROOT_PATHS.LOGS}$`),
      paymentManager: new RegExp(`^${ROOT_PATHS.PAYMENT_MANAGER}$`),
    };
  }, []);

  // Dirty check and redirect to the correct page
  const handleClick = (url: string) => {
    const isMatch = (regex: RegExp) => regex.test(currentPath);

    const redirectAndOpenModal = (
      redirectAction: Function,
      modalAction: Function
    ) => {
      dispatch(redirectAction(url));
      dispatch(modalAction(true));
    };

    if (isDirty_Member_Personal && isMatch(routeRegex.members)) {
      redirectAndOpenModal(setMember_Redirect_URL, setIsOpenUnsavedModal);
    } else if (isDirty_Staff_Personal && isMatch(routeRegex.staffs)) {
      redirectAndOpenModal(setStaff_Redirect_URL, setStaffUnsavedModal);
    } else if (isDirty_Org_Details && isMatch(routeRegex.organizations)) {
      redirectAndOpenModal(setOrg_Redirect_URL, setOrgUnsavedModal);
    } else if (
      currentPath === ROOT_PATHS.LOCALISATION &&
      locale_newLocaleFileUploaded
    ) {
      redirectAndOpenModal(setMember_Redirect_URL, setLocaleIsOpenUnsavedModal);
    } else if (
      (currentPath.startsWith(`${ROOT_PATHS.CERTIFICATE}/`) ||
        currentPath === ROOT_PATHS.ADD_CERTIFICATE) &&
      hasCertificateChanged
    ) {
      redirectAndOpenModal(
        setMember_Redirect_URL,
        setIsOpenCertificateUnsavedModal
      );
    } else {
      navigate(url);
    }
  };

  // Select the sidebar menu item based on the current path
  useEffect(() => {
    if (!currentPath || !apiRef.current) return;

    const addressMap = [
      { regex: routeRegex.reports, itemId: 'reports' },
      { regex: routeRegex.members, itemId: 'members' },
      { regex: routeRegex.staffs, itemId: 'staff' },
      { regex: routeRegex.organizations, itemId: 'organizations' },
      { regex: routeRegex.home, itemId: 'home' },
      { regex: routeRegex.certificates, itemId: 'certifications' },
      { regex: routeRegex.trainings, itemId: 'trainings' },
      { regex: routeRegex.customFields, itemId: 'customFields' },
      { regex: routeRegex.assetTypes, itemId: 'assetTypes' },
      { regex: routeRegex.assetTypeList, itemId: 'assetTypeList' },
      { regex: routeRegex.enums, itemId: 'enums' },
      { regex: routeRegex.listManagement, itemId: 'listManagement' },
      { regex: routeRegex.licenses, itemId: 'licenses' },
      { regex: routeRegex.localization, itemId: 'localisation' },
      { regex: routeRegex.countries, itemId: 'countries' },
      { regex: routeRegex.units, itemId: 'units' },
      { regex: routeRegex.cultures, itemId: 'cultures' },
      { regex: routeRegex.consents, itemId: 'consents' },
      { regex: routeRegex.contractTypes, itemId: 'contractTypes' },
      { regex: routeRegex.trainingTopics, itemId: 'trainingTopics' },
      { regex: routeRegex.functions, itemId: 'functions' },
      { regex: routeRegex.bulkUploadRequest, itemId: 'bulkUploadRequest' },
      { regex: routeRegex.certificateTypes, itemId: 'certificateTypes' },
      { regex: routeRegex.logs, itemId: 'logs' },
      { regex: routeRegex.onHand, itemId: 'onHand' },
      { regex: routeRegex.history, itemId: 'history' },
      { regex: routeRegex.processing, itemId: 'processing' },
      { regex: routeRegex.bulkPurchase, itemId: 'bulkPurchase' },
      { regex: routeRegex.paymentManager, itemId: 'paymentManager' },
    ];

    const selectedItem = addressMap.find(({ regex }) =>
      regex.test(currentPath)
    );

    (apiRef.current as any)?.selectItem({
      itemId: selectedItem?.itemId || '',
      shouldBeSelected: true,
    });
  }, [currentPath, apiRef, routeRegex]);

  // Sidebar menu items
  const sidebarMenus = [
    {
      id: 'home',
      name: messages({ id: 'HOME' }),
      icon: <img src={home} alt='' />,
      onClick: () => {
        handleClick(ROOT_PATHS.ROOT);
      },
    },
    {
      id: 'reports',
      name: messages({ id: 'REPORTS' }),
      icon: <img src={reports} alt='' />,
      onClick: () => {
        handleClick(ROOT_PATHS.REPORTS);
      },
    },
    {
      id: 'dataManagment',
      name: messages({ id: 'DATA_MANAGEMENT' }),
      expanded: true,
      disabled: !perms.showDataManagerPerms,
      sub: [
        {
          id: 'members',
          name: messages({ id: 'MEMBERS' }),
          icon: <img src={group} alt='' />,
          disabled: !perms.hasIdentityPerm || !perms.hasOrgPerm,
          onClick: () => {
            if (!perms.hasIdentityPerm || !perms.hasOrgPerm) return;
            handleClick(ROOT_PATHS.MEMBERS);
          },
        },
        {
          id: 'staff',
          name: messages({ id: 'STAFF' }),
          icon: <img src={badge} alt='' />,
          disabled:
            !flags.staffOrgs || !flags.viewStaff || !perms.hasIdentityPerm,
          onClick: () => {
            if (!flags.staffOrgs || !flags.viewStaff || !perms.hasIdentityPerm)
              return;
            handleClick(ROOT_PATHS.STAFF);
          },
        },
        {
          id: 'organizations',
          name: messages({ id: 'ORGANIZATIONS' }),
          icon: <img src={domain} alt='' />,
          disabled: !flags.staffOrgs || !perms.hasOrgPerm,
          onClick: () => {
            if (!flags.staffOrgs || !perms.hasOrgPerm) return;
            handleClick(ROOT_PATHS.ORGANISATIONS);
          },
        },
        {
          id: 'certifications',
          name: messages({ id: 'CERTIFICATIONS' }),
          icon: <img src={task} alt='' />,
          disabled: !flags.certificate || !perms.hasCertificatePerm,
          onClick: () => {
            if (!flags.certificate || !perms.hasCertificatePerm) return;
            handleClick(ROOT_PATHS.CERTIFICATES);
          },
        },
        {
          id: 'trainings',
          name: messages({ id: 'TRAININGS' }),
          icon: <MenuBookOutlinedIcon />,
          disabled: !perms.hasTrainingPerm,
          onClick: () => {
            if (!perms.hasTrainingPerm) return;
            handleClick(ROOT_PATHS.TRAININGS);
          },
        },
      ],
    },
    {
      id: 'operations',
      name: messages({ id: 'OPERATIONS_MANAGEMENT' }),
      expanded: true,
      disabled: !perms.showOperationsMenu || !flags.inventoryManagement,
      sub: [
        {
          id: 'onHand',
          name: messages({ id: 'BATCH_ON_HAND' }),
          icon: <InventoryOutlined />,
          disabled: !flags.inventoryManagement,
          onClick: () => {
            if (!flags.inventoryManagement) return;
            handleClick(ROOT_PATHS.ON_HAND);
          },
        },
        {
          id: 'processing',
          name: messages({ id: 'BATCH_PROCESSING' }),
          icon: <CachedOutlined />,
          disabled: !flags.inventoryManagement,
          onClick: () => {
            if (!flags.inventoryManagement) return;
            handleClick(ROOT_PATHS.PROCESSING);
          },
        },
        {
          id: 'history',
          name: messages({ id: 'BATCH_HISTORY' }),
          icon: <HistoryIcon />,
          disabled: !flags.inventoryManagement,
          onClick: () => {
            if (!flags.inventoryManagement) return;
            handleClick(ROOT_PATHS.HISTORY);
          },
        },
        {
          id: 'bulkPurchase',
          name: messages({ id: 'BULK_PURCHASE' }),
          icon: <ShoppingBasketOutlined />,
          disabled: !flags.inventoryManagement,
          onClick: () => {
            if (!flags.inventoryManagement) return;
            handleClick(ROOT_PATHS.BULK_PURCHASE);
          },
        },
        {
          id: 'paymentManager',
          name: messages({ id: 'PAYMENT_MANAGER' }),
          icon: <PaymentsOutlinedIcon />,
          disabled: !perms.hasPaymentManagerPerm || !flags.paymentsManagement,
          onClick: () => {
            if (!perms.hasPaymentManagerPerm || !flags.paymentsManagement)
              return;
            handleClick(ROOT_PATHS.PAYMENT_MANAGER);
          },
        },
      ],
    },
    {
      id: 'adminstration',
      name: messages({ id: 'SIDEBAR_ADMIN_FEATURES' }),
      expanded: true,
      messageNumber: 5,
      disabled: !perms.showAdminMenu,
      sub: [
        {
          id: 'enums',
          name: messages({ id: 'ENUMS_MANAGEMENT' }),
          icon: <CategoryOutlinedIcon />,
          disabled: !perms.hasEnums,
          onClick: () => {
            if (!perms.hasEnums) return;
            handleClick(ROOT_PATHS.ENUMS);
          },
        },
        {
          id: 'customFields',
          name: messages({ id: 'CUSTOMFIELDS' }),
          icon: <SettingsOutlinedIcon />,
          disabled: !canEditCustomFields || !perms.hasCustomFieldPerm,
          onClick: () => {
            if (!canEditCustomFields || !perms.hasCustomFieldPerm) return;
            handleClick(ROOT_PATHS.CUSTOM_FIELDS);
          },
        },
        {
          id: 'assetTypes',
          name: messages({ id: 'ASSETTYPES' }),
          icon: <ChecklistOutlinedIcon />,
          disabled: !perms.hasAssetTypesPerm,
          onClick: () => {
            if (!perms.hasAssetTypesPerm) return;
            handleClick(ROOT_PATHS.ASSET_TYPES);
          },
        },
        {
          id: 'listManagement',
          name: messages({ id: 'LIST_MANAGEMENT' }),
          icon: <RecentActorsOutlinedIcon />,
          disabled: !perms.hasListManagementPerm,
          onClick: () => {
            if (!perms.hasListManagementPerm) return;
            handleClick(ROOT_PATHS.LIST_MANAGEMENT);
          },
        },
        {
          id: 'consents',
          name: messages({ id: 'CONSENTS' }),
          icon: <HandshakeIcon />,
          disabled: !perms.hasConsents,
          onClick: () => {
            if (!perms.hasConsents) return;
            handleClick(ROOT_PATHS.CONSENTS);
          },
        },
        {
          id: 'contractTypes',
          name: messages({ id: 'CONTRACT_TYPES' }),
          icon: <SummarizeOutlinedIcon />,
          disabled: !perms.hasContractTypes,
          onClick: () => {
            if (!perms.hasContractTypes) return;
            handleClick(ROOT_PATHS.CONTRACT_TYPES);
          },
        },
        {
          id: 'certificateTypes',
          name: messages({ id: 'CERTIFICATE_TYPES' }),
          icon: <AdminPanelSettingsOutlinedIcon />,
          disabled: !perms.hasCertificateTypesPerm,
          onClick: () => {
            if (!perms.hasCertificateTypesPerm) return;
            handleClick(ROOT_PATHS.CERTIFICATE_TYPES);
          },
        },
        {
          id: 'trainingTopics',
          name: messages({ id: 'TRAINING_TOPICS' }),
          icon: <TopicOutlined />,
          disabled: !perms.hasTrainingTopics,
          onClick: () => {
            if (!perms.hasTrainingTopics) return;
            handleClick(ROOT_PATHS.TRAINING_TOPICS);
          },
        },
        {
          id: 'functions',
          name: messages({ id: 'FUNCTIONS' }),
          icon: <FunctionsOutlined />,
          disabled: !perms.hasFunctions,
          onClick: () => {
            if (!perms.hasFunctions) return;
            handleClick(ROOT_PATHS.FUNCTIONS);
          },
        },
        {
          id: 'bulkUploadRequest',
          name: messages({ id: 'BULK_UPLOAD_REQUEST' }),
          icon: <DriveFolderUploadOutlinedIcon />,
          disabled: !perms.hasBulkUploadRequestPerm,
          onClick: () => {
            if (!perms.hasBulkUploadRequestPerm) return;
            handleClick(ROOT_PATHS.BULK_UPLOAD_REQUEST);
          },
        },
        {
          id: 'logs',
          name: messages({ id: 'LOGS' }),
          icon: <HistoryOutlinedIcon />,
          disabled: !perms.hasLogsPerm,
          onClick: () => {
            if (!perms.hasLogsPerm) return;
            handleClick(ROOT_PATHS.LOGS);
          },
        },
      ],
    },
    {
      id: 'configuration',
      name: messages({ id: 'SIDEBAR_ADMIN_CONFIGURATION' }),
      expanded: true,
      messageNumber: 5,
      disabled: !perms.showConfigurationMenu,
      sub: [
        {
          id: 'units',
          name: messages({ id: 'UNITS' }),
          icon: <WidgetsOutlinedIcon />,
          disabled: !perms.hasUnits,
          onClick: () => {
            if (!perms.hasUnits) return;
            handleClick(ROOT_PATHS.UNITS);
          },
        },
        {
          id: 'countries',
          name: messages({ id: 'COUNTRIES' }),
          icon: <OutlinedFlagIcon />,
          disabled: !perms.hasCountries,
          onClick: () => {
            if (!perms.hasCountries) return;
            handleClick(ROOT_PATHS.COUNTRIES);
          },
        },
        {
          id: 'cultures',
          name: messages({ id: 'CULTURES' }),
          icon: <PublicIcon />,
          disabled: !perms.hasCultures,
          onClick: () => {
            if (!perms.hasCultures) return;
            handleClick(ROOT_PATHS.CULTURES);
          },
        },
        {
          id: 'assetTypeList',
          name: messages({ id: 'ASSET_TYPE_LIST' }),
          icon: <ListAltOutlinedIcon />,
          disabled: !perms.hasAssetTypeListPerm,
          onClick: () => {
            if (!perms.hasAssetTypeListPerm) return;
            handleClick(ROOT_PATHS.ASSET_TYPE_LIST);
          },
        },
        {
          id: 'localisation',
          name: messages({ id: 'LOCALISATION' }),
          icon: <LanguageIcon />,
          disabled: !perms.hasLocalizationPerm,
          onClick: () => {
            if (!perms.hasLocalizationPerm) return;
            handleClick(ROOT_PATHS.LOCALISATION);
          },
        },
        {
          id: 'licenses',
          name: messages({ id: 'LICENSES' }),
          icon: <SecurityIcon />,
          disabled: !perms.hasLicensePerm,
          onClick: () => {
            if (!perms.hasLicensePerm) return;
            handleClick(ROOT_PATHS.LICENSES);
          },
        },
      ],
    },
  ];

  return <AgMainNavigation data={sidebarMenus} apiRef={apiRef} />;
};
export default SidebarVariation1;
